import Page from "@/components/layout/Page";
import * as Styled from "./styled";
import useApi from "@/contexts/ApiContext";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import LoadingScreen from "@/components/shared-ui/LoadingScreen";
import IntroPanel from "@/components/shared-ui/IntroPanel";
import {
  communicationTimelinessLookup,
  currentEngagementLookup,
  getTransferProviderStatus,
  relationshipStrengthLookup,
  type TransferProviderStatus,
} from "./statusLookup";
import ContactCard from "@/components/shared-ui/ContactCard";
import { Link } from "react-router-dom";

export const StatusPill = ({
  label,
  colour,
  backgroundColour,
}: TransferProviderStatus) => (
  <Styled.Status $colour={colour} $backgroundColour={backgroundColour}>
    {label}
  </Styled.Status>
);

export const StatusDot = ({ label, colour, omitLabel }: TransferProviderStatus & {omitLabel?: boolean}) => (
  <Styled.StatusDot $colour={colour}>{omitLabel ? null : label}</Styled.StatusDot>
);

const keyFields = [
  {
    label: "Relationship strength",
    statuses: Object.values(relationshipStrengthLookup),
  },
  {
    label: "Current engagement",
    statuses: Object.values(currentEngagementLookup),
  },
  {
    label: "Communication timeliness",
    statuses: Object.values(communicationTimelinessLookup),
  },
];

const TransferProviderList = () => {
  const api = useApi();
  const { data: transferProviders } = useQuery({
    queryKey: ["get-transfer-providers"],
    queryFn: () => api.transferProviders.getAll(),
    placeholderData: keepPreviousData,
  });

  if (!transferProviders) {
    return <LoadingScreen />;
  }

  return (
    <Page title="Transfer providers" featureBackground>
      <IntroPanel
        heading="Transfer providers"
        description="<p>These playbooks bring together the key provider-specific processes, requirements and details users need to know when submitting transfers.</p><p>Use them to understand what to expect that will help reduce avoidable delays and ensure transfers progress smoothly. We’ll keep them updated as provider processes change.</p>"
      ></IntroPanel>
      <Styled.TransferProviderList>
        {transferProviders.map((provider) => (
          <Link
            to={`/transfer-providers/${provider.slug}`}
            key={provider.id}
          >
            <Styled.TransferProviderItem>
              <Styled.ItemInnerContainer>
                <Styled.Logo>
                  <img src={provider.logo.url} alt={provider.name} />
                </Styled.Logo>
                <Styled.ItemDetails>
                  <Styled.ItemField>
                    <span>Name:</span>
                    <strong>{provider.name}</strong>
                  </Styled.ItemField>
                  <Styled.ItemField>
                    <span>Relationship strength:</span>
                    <StatusDot
                      {...getTransferProviderStatus(
                        relationshipStrengthLookup,
                        provider.relationshipStrength,
                      )}
                    />
                  </Styled.ItemField>
                  <Styled.ItemField>
                    <span>Current engagement:</span>
                    <StatusDot
                      {...getTransferProviderStatus(
                        currentEngagementLookup,
                        provider.currentEngagement,
                      )}
                    />
                  </Styled.ItemField>
                  <Styled.ItemField>
                    <span>Communication timeliness:</span>
                    <StatusDot
                      {...getTransferProviderStatus(
                        communicationTimelinessLookup,
                        provider.communicationTimeliness,
                      )}
                    />
                  </Styled.ItemField>
                </Styled.ItemDetails>
              </Styled.ItemInnerContainer>
            </Styled.TransferProviderItem>
          </Link>
        ))}
      </Styled.TransferProviderList>
      <Styled.Key>
        <Styled.KeyHeading>Framework</Styled.KeyHeading>
        {keyFields.map(({ label, statuses }) => (
          <Styled.KeyField key={label}>
            <Styled.KeyFieldHeading>{label}</Styled.KeyFieldHeading>
            <Styled.KeyStatuses>
              {statuses.map((status) => (
                <Styled.KeyStatus key={status.label}>
                  <StatusPill {...status} />
                  <span>{status.description}</span>
                </Styled.KeyStatus>
              ))}
            </Styled.KeyStatuses>
          </Styled.KeyField>
        ))}
      </Styled.Key>

      <ContactCard />
    </Page>
  );
};

export default TransferProviderList;
